import { Random } from '../utils/Random.js';
import { playNoiseBurst, playTone } from './proceduralSynth.js';

/**
 * NPC crowd bed — filtered noise + sparse digital murmur scaled by nearby agents.
 */
export class CrowdAudio {
  /**
   * @param {AudioContext} ctx
   * @param {GainNode} bus
   * @param {import('../config.js').CONFIG['audio']['crowd']} config
   * @param {number} seed
   */
  constructor(ctx, bus, config, seed) {
    this._ctx = ctx;
    this._bus = bus;
    this._config = config;
    this._rng = new Random(seed);
    this._density = 0;
    this._chirpTimer = 0;
    this._started = false;

    /** @type {AudioBufferSourceNode | null} */
    this._noise = null;
    /** @type {GainNode | null} */
    this._bedGain = null;
    /** @type {BiquadFilterNode | null} */
    this._filter = null;
  }

  start() {
    if (this._started) return;
    this._started = true;

    const length = this._ctx.sampleRate * 2;
    const buffer = this._ctx.createBuffer(1, length, this._ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * 0.4;
    }

    this._noise = this._ctx.createBufferSource();
    this._noise.buffer = buffer;
    this._noise.loop = true;

    this._filter = this._ctx.createBiquadFilter();
    this._filter.type = 'bandpass';
    this._filter.frequency.value = this._config.filterHz;
    this._filter.Q.value = this._config.filterQ;

    this._bedGain = this._ctx.createGain();
    this._bedGain.gain.value = 0;

    this._noise.connect(this._filter);
    this._filter.connect(this._bedGain);
    this._bedGain.connect(this._bus);
    this._noise.start();

    this._chirpTimer = this._rng.range(
      this._config.chirpIntervalMin,
      this._config.chirpIntervalMax,
    );
  }

  /**
   * @param {number} delta
   * @param {{
   *   inSimulation?: boolean;
   *   nearbyNpcCount?: number;
   * }} state
   */
  update(delta, state) {
    if (!this._started || !this._bedGain) return;

    const count = state.inSimulation ? (state.nearbyNpcCount ?? 0) : 0;
    const target = Math.min(
      1,
      count / Math.max(1, this._config.fullDensityCount),
    );
    this._density += (target - this._density) * Math.min(1, delta * 3.5);

    const bed =
      this._density * this._config.bedGain * (count > 0 ? 1 : 0);
    this._bedGain.gain.value += (bed - this._bedGain.gain.value) * Math.min(1, delta * 6);

    if (this._filter) {
      const freq =
        this._config.filterHz +
        this._density * this._config.filterSweep * 0.35;
      this._filter.frequency.value += (freq - this._filter.frequency.value) * Math.min(1, delta * 4);
    }

    if (count <= 0 || this._density < 0.04) return;

    this._chirpTimer -= delta;
    if (this._chirpTimer > 0) return;
    this._chirpTimer = this._rng.range(
      this._config.chirpIntervalMin,
      this._config.chirpIntervalMax,
    );
    if (this._rng.next() > this._config.chirpChance * this._density) return;
    this._playChirp();
  }

  dispose() {
    if (this._noise) {
      try {
        this._noise.stop();
      } catch {
        /* noop */
      }
      this._noise.disconnect();
      this._noise = null;
    }
    this._filter?.disconnect();
    this._filter = null;
    this._bedGain?.disconnect();
    this._bedGain = null;
    this._started = false;
  }

  _playChirp() {
    const gain = this._config.chirpGain * this._density * this._rng.range(0.7, 1.2);
    playTone(this._ctx, this._bus, {
      type: 'triangle',
      frequency: this._rng.range(320, 920),
      detune: this._rng.range(-80, 80),
      duration: this._rng.range(0.04, 0.09),
      gain,
      attack: 0.004,
      decay: 0.05,
    });
    playNoiseBurst(this._ctx, this._bus, {
      duration: 0.035,
      gain: gain * 0.45,
      filterFreq: this._rng.range(500, 1400),
      filterQ: 0.85,
    });
  }
}
